import React, { useState } from 'react';
import { DatePicker, DefaultButton, Dropdown, IDropdownOption, Panel, PanelType, PrimaryButton, Spinner, SpinnerSize, Stack, TextField } from '@fluentui/react';
import moment from 'moment';
import { useStore } from '../../../../stores/root';
import { IOperationItem } from '../types';
import Label from '../../../typography/label';
import Text from '../../../typography/text';
import GeneralService from '../../../../services/general';
import ErrorService from '../../../../services/general/error';
import ShipScheduleDelaysService from '../../../../services/data/ships/shipScheduleDelays';
import OrderTrackingService from '../../../../services/orders/trackings/orderTracking';
import { faCheck, faClock, faXmarkCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface IRescheduleModalProps {
    item: IOperationItem;
    isOpen: boolean;
    onDismiss(refresh?: boolean): void;
}

const RESCHEDULE_REASONS: IDropdownOption[] = [
    { key: 'Kapal Delay / Belum Sandar', text: 'Kapal Delay / Belum Sandar' },
    { key: 'Cuaca Buruk / Ombak Tinggi', text: 'Cuaca Buruk / Ombak Tinggi' },
    { key: 'Perubahan Jadwal Pelayaran (Vendor)', text: 'Perubahan Jadwal Pelayaran (Vendor)' },
    { key: 'Kepadatan Pelabuhan (Congestion)', text: 'Kepadatan Pelabuhan (Congestion)' },
    { key: 'Kendala Lapangan / Depo / Gudang', text: 'Kendala Lapangan / Depo / Gudang' },
    { key: 'Kesiapan Armada Truk / Driver', text: 'Kesiapan Armada Truk / Driver' },
    { key: 'Permintaan Pelanggan / Customer', text: 'Permintaan Pelanggan / Customer' },
    { key: 'Lainnya', text: 'Lainnya (Tuliskan di Catatan)' },
];

const RescheduleModal: React.FC<IRescheduleModalProps> = ({ item, isOpen, onDismiss }) => {
    const { banner } = useStore();
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [newDepartureDate, setNewDepartureDate] = useState<Date>(
        moment(item.scheduledDate).isValid() ? moment(item.scheduledDate).toDate() : new Date()
    );
    const [newArrivalDate, setNewArrivalDate] = useState<Date | undefined>(
        item.estimatedArrival && moment(item.estimatedArrival).isValid()
            ? moment(item.estimatedArrival).toDate()
            : moment(item.scheduledDate).add(2, 'days').toDate()
    );
    const [reason, setReason] = useState<string>(RESCHEDULE_REASONS[0].key as string);
    const [notes, setNotes] = useState<string>('');

    const isShip = item.itemType === 'ship';

    const handleSubmit = async () => {
        try {
            setSubmitting(true);
            const depIso = moment(newDepartureDate).toISOString();
            const arrIso = newArrivalDate ? moment(newArrivalDate).toISOString() : depIso;

            if (isShip) {
                // Ship schedule delay
                const fd = new FormData();
                fd.append('scheduleId', item.rawId);
                fd.append('newEstimatedDeparture', depIso);
                fd.append('newEstimatedArrival', arrIso);
                if (reason) fd.append('reason', reason);
                if (notes) fd.append('notes', notes);

                await ShipScheduleDelaysService.new(fd);
                // Di dalam method handleSubmit() pada reschedule.tsx:
            } else if (item.orderId) {
                const fd = new FormData();

                // PERBAIKAN: Tambahkan parameter type agar backend tahu mana yang di-update
                const trackingType = item.rawObject?.type || (item.activityType === 'unloading' ? 'unload' : (item.activityType === 'loading' ? 'load' : 'truck'));
                fd.append('type', trackingType);

                if (item.activityType === 'loading') {
                    fd.append('estimatedStartDate', depIso);
                } else if (item.activityType === 'unloading') {
                    fd.append('estimatedStartDate', depIso);
                } else if (item.activityType === 'dooring') {
                    fd.append('estimatedDepartureDate', depIso);
                    fd.append('estimatedArrivalDate', arrIso);
                }

                if (notes || reason) {
                    fd.append('notes', `[Reschedule: ${reason}] ${notes || ''}`.trim());
                }

                await OrderTrackingService.update(item.orderId, item.rawId, fd);
            }


            banner.add({
                key: 'reschedule_success',
                variant: 'success',
                icon: faCheck,
                text: `Berhasil menjadwalkan ulang ${item.activityLabel}: ${item.shipName || item.orderNumber || ''}`
            });

            onDismiss(true);
        } catch (e) {
            banner.add({
                key: 'reschedule_error',
                variant: 'error',
                icon: faXmarkCircle,
                text: 'Gagal melakukan reschedule. Error: ' + ErrorService.getMessage(e)
            });
            setSubmitting(false);
        }
    };

    return (
        <Panel
            headerText={`Reschedule Activity`}
            isOpen={isOpen}
            type={PanelType.medium}
            onDismiss={() => onDismiss(false)}
            isFooterAtBottom={true}
            onRenderFooterContent={() => (
                <Stack horizontal tokens={{ childrenGap: 10 }}>
                    {!submitting ? (
                        <>
                            <PrimaryButton
                                text="Simpan Jadwal Baru"
                                iconProps={{ iconName: 'Calendar' }}
                                onClick={handleSubmit}
                            />
                            <DefaultButton text="Batal" onClick={() => onDismiss(false)} />
                        </>
                    ) : (
                        <Spinner size={SpinnerSize.medium} label="Menyimpan jadwal baru..." labelPosition="right" />
                    )}
                </Stack>
            )}
        >
            <Stack tokens={{ childrenGap: 18 }} styles={{ root: { marginTop: 15 } }}>
                {/* Current Item Summary Card */}
                <Stack
                    styles={{
                        root: {
                            background: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            padding: '14px 16px',
                        }
                    }}
                    tokens={{ childrenGap: 8 }}
                >
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                        <span
                            style={{
                                background: item.activityColor + '20',
                                color: item.activityColor,
                                border: `1px solid ${item.activityColor}40`,
                                padding: '2px 8px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: 700,
                            }}
                        >
                            <FontAwesomeIcon icon={item.activityIcon} style={{ marginRight: 5 }} />
                            {item.activityLabel}
                        </span>
                        {item.isOverdue && (
                            <span
                                style={{
                                    background: '#fee2e2',
                                    color: '#b91c1c',
                                    border: '1px solid #fecaca',
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    fontWeight: 700,
                                }}
                            >
                                Overdue {item.overdueDays ? `${item.overdueDays} hari` : ''}
                            </span>
                        )}
                    </Stack>

                    <Text size="large" style={{ fontWeight: 600, color: '#0f172a' }}>
                        {item.shipName ? `${item.shipName} ${item.voy ? `(Voy: ${item.voy})` : ''}` : item.orderNumber || '-'}
                    </Text>

                    {item.locationText && (
                        <Text size="small" style={{ color: '#64748b' }}>
                            📍 {item.locationText}
                        </Text>
                    )}

                    <Stack horizontal tokens={{ childrenGap: 20 }} styles={{ root: { marginTop: 4 } }}>
                        <div>
                            <Label size="xsmall">Jadwal Saat Ini (ETD / Mulai):</Label>
                            <Text size="small" style={{ fontWeight: 600 }}>
                                {item.scheduledDateFormatted}
                            </Text>
                        </div>
                        {item.estimatedArrival && (
                            <div>
                                <Label size="xsmall">Perkiraan Tiba (ETA):</Label>
                                <Text size="small" style={{ fontWeight: 600 }}>
                                    {moment(item.estimatedArrival).format('DD/MM/YYYY')}
                                </Text>
                            </div>
                        )}
                    </Stack>
                </Stack>

                {/* Form Inputs */}
                <Stack tokens={{ childrenGap: 14 }}>
                    <DatePicker
                        label={isShip ? 'Tanggal Keberangkatan Baru (New ETD) *' : 'Tanggal Mulai Baru *'}
                        value={newDepartureDate}
                        onSelectDate={(date) => {
                            if (date) setNewDepartureDate(date);
                        }}
                        formatDate={GeneralService.formatDate}
                        isRequired
                    />

                    {isShip && (
                        <DatePicker
                            label="Perkiraan Tiba Baru (New ETA) *"
                            value={newArrivalDate}
                            onSelectDate={(date) => {
                                if (date) setNewArrivalDate(date);
                            }}
                            formatDate={GeneralService.formatDate}
                            isRequired
                        />
                    )}

                    <Dropdown
                        label="Alasan Reschedule / Keterlambatan *"
                        options={RESCHEDULE_REASONS}
                        selectedKey={reason}
                        onChange={(_, opt) => {
                            if (opt) setReason(opt.key as string);
                        }}
                        required
                    />

                    <TextField
                        label="Catatan Tambahan (Opsional)"
                        multiline
                        rows={3}
                        value={notes}
                        onChange={(_, val) => setNotes(val || '')}
                        placeholder="Tambahkan keterangan pendukung terkait perubahan jadwal..."
                    />
                </Stack>
            </Stack>
        </Panel>
    );
};

export default RescheduleModal;
